import React, { useState, useEffect } from 'react';
import { useLanguage, useAuth, api } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { 
  Stethoscope, 
  AlertTriangle, 
  Shield, 
  Heart, 
  ChefHat, 
  Loader2,
  Check,
  X,
  Info,
  Activity,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

// ==================== CLINICAL PROFILES ====================
// 6 profils majeurs avec leurs filtres spécifiques

const CLINICAL_PROFILES = [
  { 
    value: 'oncology', 
    labelFr: '🎗️ Oncologie (Cancer/Chimio/Radio)', 
    labelEn: '🎗️ Oncology (Cancer/Chemo/Radio)',
    descFr: 'Gère la fatigue, nausées, altération du goût',
    descEn: 'Manages fatigue, nausea, taste changes',
    autoRestrictions: ['no_strong_odors', 'soft_textures', 'small_portions', 'cold_foods_ok'],
    color: 'pink'
  },
  { 
    value: 'gastrointestinal', 
    labelFr: '🩺 Gastro-Intestinal (Crohn/Colite/Diverticulite)', 
    labelEn: '🩺 Gastrointestinal (Crohn/Colitis/Diverticulitis)',
    descFr: 'Ne pas irriter le système digestif',
    descEn: 'Avoid digestive system irritation',
    autoRestrictions: ['low_fiber', 'no_seeds_nuts', 'no_raw_foods', 'no_spicy', 'well_cooked_only'],
    color: 'orange'
  },
  { 
    value: 'autoimmune', 
    labelFr: '🛡️ Auto-Immun (Cœliaque/Intolérances)', 
    labelEn: '🛡️ Autoimmune (Celiac/Intolerances)',
    descFr: 'Zéro tolérance aux allergènes',
    descEn: 'Zéro allergen tolerance',
    autoRestrictions: ['gluten_free_strict', 'lactose_free', 'fodmap_friendly'],
    color: 'purple'
  },
  { 
    value: 'metabolic', 
    labelFr: '📊 Métabolique (Diabète Type 1 & 2)', 
    labelEn: '📊 Metabolic (Diabetes Type 1 & 2)',
    descFr: 'Stabiliser la glycémie',
    descEn: 'Stabilize blood sugar',
    autoRestrictions: ['low_glycemic', 'controlled_carbs', 'no_added_sugar', 'stevia_monk_fruit_ok'],
    color: 'blue'
  },
  { 
    value: 'cardiovascular', 
    labelFr: '❤️ Cardiovasculaire (Hypertension/Post-infarctus)', 
    labelEn: '❤️ Cardiovascular (Hypertension/Post-MI)',
    descFr: 'Protéger le cœur et la pression artérielle',
    descEn: 'Protect heart and blood pressure',
    autoRestrictions: ['very_low_sodium', 'dash_diet', 'low_saturated_fat', 'high_omega3', 'herbs_spices_instead_salt'],
    color: 'red'
  },
  { 
    value: 'renal', 
    labelFr: '🫘 Rénal (Insuffisance rénale)', 
    labelEn: '🫘 Renal (Kidney Disease)',
    descFr: 'Ne pas surcharger les reins - le plus complexe',
    descEn: 'Avoid kidney overload - most complex',
    autoRestrictions: ['low_potassium', 'low_phosphorus', 'very_low_sodium', 'controlled_protein', 'no_bananas_tomatoes'],
    color: 'yellow'
  },
  { 
    value: 'bariatric', 
    labelFr: '⚖️ Chirurgie Bariatrique', 
    labelEn: '⚖️ Bariatric Surgery',
    descFr: 'Petites portions, haute densité nutritionnelle',
    descEn: 'Small portions, high nutrient density',
    autoRestrictions: ['tiny_portions', 'high_protein_priority', 'no_carbonation', 'soft_textures'],
    color: 'teal'
  },
  { 
    value: 'neuro_support', 
    labelFr: '🧠 Santé Cognitive / Neuro-Soutien', 
    labelEn: '🧠 Cognitive Health / Neuro-Support',
    descFr: 'Aînés, Alzheimer, troubles cognitifs — nutrition neuro-protectrice',
    descEn: 'Seniors, Alzheimer, cognitive issues — neuroprotective nutrition',
    autoRestrictions: ['small_portions', 'soft_textures', 'high_omega3', 'low_sugar'],
    color: 'purple'
  },
  { 
    value: 'custom', 
    labelFr: '📝 Autre protocole personnalisé', 
    labelEn: '📝 Other custom protocol',
    descFr: 'Configurez vos propres restrictions',
    descEn: 'Configure your own restrictions',
    autoRestrictions: [],
    color: 'gray'
  }
];

// Restrictions détaillées organisées par catégorie
const RESTRICTION_CATEGORIES = [
  {
    category: 'texture',
    labelFr: '🥄 Textures & Préparation',
    labelEn: '🥄 Textures & Préparation',
    restrictions: [
      { value: 'soft_textures', labelFr: 'Textures douces/lisses uniquement', labelEn: 'Soft/smooth textures only' },
      { value: 'well_cooked_only', labelFr: 'Aliments bien cuits seulement', labelEn: 'Well-cooked foods only' },
      { value: 'no_raw_foods', labelFr: 'Pas d\'aliments crus', labelEn: 'No raw foods' },
      { value: 'cold_foods_ok', labelFr: 'Préférer aliments froids/tempérés', labelEn: 'Prefer cold/room temp foods' },
      { value: 'small_portions', labelFr: 'Petites portions fractionnées (6/jour)', labelEn: 'Small fractioned portions (6/day)' },
      { value: 'tiny_portions', labelFr: 'Très petites portions (post-bariatrique)', labelEn: 'Very small portions (post-bariatric)' },
    ]
  },
  {
    category: 'allergens',
    labelFr: '⚠️ Allergènes & Intolérances',
    labelEn: '⚠️ Allergens & Intolerances',
    restrictions: [
      { value: 'gluten_free_strict', labelFr: '100% Sans gluten (Cœliaque)', labelEn: '100% Gluten-free (Celiac)' },
      { value: 'lactose_free', labelFr: 'Sans lactose', labelEn: 'Lactose-free' },
      { value: 'fodmap_friendly', labelFr: 'Protocole FODMAP', labelEn: 'FODMAP protocol' },
      { value: 'no_seeds_nuts', labelFr: 'Sans noix/graines entières', labelEn: 'No whole nuts/seeds' },
    ]
  },
  {
    category: 'digestive',
    labelFr: '🍃 Digestif',
    labelEn: '🍃 Digestive',
    restrictions: [
      { value: 'low_fiber', labelFr: 'Faible en fibres insolubles', labelEn: 'Low insoluble fiber' },
      { value: 'no_spicy', labelFr: 'Sans épices fortes/irritantes', labelEn: 'No strong/irritating spices' },
      { value: 'no_strong_odors', labelFr: 'Éviter odeurs fortes', labelEn: 'Avoid strong odors' },
      { value: 'no_carbonation', labelFr: 'Pas de boissons gazeuses', labelEn: 'No carbonated drinks' },
    ]
  },
  {
    category: 'metabolic',
    labelFr: '📊 Métabolique & Glycémique',
    labelEn: '📊 Metabolic & Glycemic',
    restrictions: [
      { value: 'low_glycemic', labelFr: 'Indice glycémique bas (IG bas)', labelEn: 'Low glycemic index (low GI)' },
      { value: 'controlled_carbs', labelFr: 'Glucides nets surveillés', labelEn: 'Monitored net carbs' },
      { value: 'no_added_sugar', labelFr: 'Sans sucre ajouté', labelEn: 'No added sugar' },
      { value: 'stevia_monk_fruit_ok', labelFr: 'Stevia/Fruit du moine OK', labelEn: 'Stevia/Monk fruit OK' },
    ]
  },
  {
    category: 'cardiovascular',
    labelFr: '❤️ Cardiovasculaire',
    labelEn: '❤️ Cardiovascular',
    restrictions: [
      { value: 'very_low_sodium', labelFr: 'Très faible en sodium/sel', labelEn: 'Very low sodium/salt' },
      { value: 'dash_diet', labelFr: 'Régime DASH', labelEn: 'DASH diet' },
      { value: 'low_saturated_fat', labelFr: 'Faible en gras saturés', labelEn: 'Low saturated fat' },
      { value: 'high_omega3', labelFr: 'Riche en oméga-3', labelEn: 'High omega-3' },
      { value: 'herbs_spices_instead_salt', labelFr: 'Herbes/épices au lieu du sel', labelEn: 'Herbs/spices instead of salt' },
    ]
  },
  {
    category: 'renal',
    labelFr: '🫘 Rénal',
    labelEn: '🫘 Renal',
    restrictions: [
      { value: 'low_potassium', labelFr: 'Faible en potassium', labelEn: 'Low potassium' },
      { value: 'low_phosphorus', labelFr: 'Faible en phosphore', labelEn: 'Low phosphorus' },
      { value: 'controlled_protein', labelFr: 'Protéines contrôlées', labelEn: 'Controlled protein' },
      { value: 'no_bananas_tomatoes', labelFr: 'Éviter bananes/tomates', labelEn: 'Avoid bananas/tomatoes' },
    ]
  },
  {
    category: 'protein',
    labelFr: '🥩 Protéines',
    labelEn: '🥩 Protein',
    restrictions: [
      { value: 'high_protein_priority', labelFr: 'Priorité haute aux protéines', labelEn: 'High protein priority' },
      { value: 'lean_proteins_only', labelFr: 'Protéines maigres seulement', labelEn: 'Lean proteins only' },
    ]
  }
];

const ClinicalModeSettings = ({ onUpdate }) => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  
  // Clinical mode state - NOW SUPPORTS MULTIPLE PROFILES
  const [clinicalMode, setClinicalMode] = useState({
    enabled: false,
    disclaimer_accepted: false,
    condition_types: [], // Changed to array for multiple selection
    custom_restrictions: [],
    dietary_notes: '',
    healthcare_provider: '',
    custom_macros: null,
    medical_restrictions: [],
    daily_sodium_limit: null,
    daily_sugar_limit: null,
    blood_pressure_tracking: false,
    locked_by_coach: false
  });
  
  // Disclaimer checkboxes
  const [acceptNotMedical, setAcceptNotMedical] = useState(false);
  const [acceptConsultHealthcare, setAcceptConsultHealthcare] = useState(false);

  useEffect(() => {
    fetchClinicalMode();
  }, []);

  const fetchClinicalMode = async () => {
    try {
      const response = await api.get('/users/clinical-mode');
      const data = response.data.clinical_mode;
      // Migrate old single condition_type to array format
      if (data.condition_type && !data.condition_types) {
        data.condition_types = [data.condition_type];
      }
      setClinicalMode({
        ...data,
        condition_types: data.condition_types || []
      });
    } catch (error) {
      console.error('Error fetching clinical mode:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle a clinical profile (supports multiple selection)
  const toggleProfile = (profileValue) => {
    setClinicalMode(prev => {
      const isSelected = prev.condition_types.includes(profileValue);
      let newTypes;
      let newRestrictions = [...prev.custom_restrictions];
      
      if (isSelected) {
        // Remove profile and its auto-restrictions
        newTypes = prev.condition_types.filter(t => t !== profileValue);
        const profile = CLINICAL_PROFILES.find(p => p.value === profileValue);
        if (profile) {
          newRestrictions = newRestrictions.filter(r => !profile.autoRestrictions.includes(r));
        }
      } else {
        // Add profile and merge auto-restrictions
        newTypes = [...prev.condition_types, profileValue];
        const profile = CLINICAL_PROFILES.find(p => p.value === profileValue);
        if (profile) {
          profile.autoRestrictions.forEach(r => {
            if (!newRestrictions.includes(r)) {
              newRestrictions.push(r);
            }
          });
        }
      }
      
      return {
        ...prev,
        condition_types: newTypes,
        custom_restrictions: newRestrictions
      };
    });
  };

  const handleEnableClick = () => {
    if (!clinicalMode.disclaimer_accepted) {
      setShowDisclaimerModal(true);
    } else {
      toggleClinicalMode(true);
    }
  };

  const acceptDisclaimer = async () => {
    if (!acceptNotMedical || !acceptConsultHealthcare) {
      toast.error(language === 'fr' 
        ? 'Veuillez cocher les deux cases pour continuer'
        : 'Please check both boxes to continue');
      return;
    }

    setSaving(true);
    try {
      await api.post('/users/clinical-mode/disclaimer', {
        accept_not_medical_advice: acceptNotMedical,
        accept_consult_healthcare: acceptConsultHealthcare
      });
      
      setClinicalMode(prev => ({ ...prev, disclaimer_accepted: true }));
      setShowDisclaimerModal(false);
      toast.success(language === 'fr' 
        ? 'Décharge acceptée. Mode clinique activé.'
        : 'Disclaimer accepted. Clinical mode enabled.');
      
      // Now enable clinical mode
      await toggleClinicalMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error accepting disclaimer');
    } finally {
      setSaving(false);
    }
  };

  const toggleClinicalMode = async (enabled) => {
    if (enabled && !clinicalMode.disclaimer_accepted) {
      setShowDisclaimerModal(true);
      return;
    }

    setSaving(true);
    try {
      if (enabled) {
        await api.put('/users/clinical-mode/config', {
          enabled: true,
          condition_types: clinicalMode.condition_types || [],
          custom_restrictions: clinicalMode.custom_restrictions || [],
          dietary_notes: clinicalMode.dietary_notes || '',
          healthcare_provider: clinicalMode.healthcare_provider || '',
          medical_restrictions: clinicalMode.medical_restrictions || [],
          daily_sodium_limit: clinicalMode.daily_sodium_limit || null,
          daily_sugar_limit: clinicalMode.daily_sugar_limit || null,
          blood_pressure_tracking: clinicalMode.blood_pressure_tracking || false,
          locked_by_coach: clinicalMode.locked_by_coach || false
        });
      } else {
        await api.delete('/users/clinical-mode');
      }
      
      setClinicalMode(prev => ({ ...prev, enabled }));
      toast.success(enabled 
        ? (language === 'fr' ? 'Mode clinique activé' : 'Clinical mode enabled')
        : (language === 'fr' ? 'Mode clinique désactivé' : 'Clinical mode disabled'));
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating clinical mode');
    } finally {
      setSaving(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      await api.put('/users/clinical-mode/config', {
        enabled: clinicalMode.enabled,
        condition_types: clinicalMode.condition_types,
        custom_restrictions: clinicalMode.custom_restrictions,
        dietary_notes: clinicalMode.dietary_notes,
        healthcare_provider: clinicalMode.healthcare_provider,
        custom_calories_min: clinicalMode.custom_macros?.calories_min,
        custom_calories_max: clinicalMode.custom_macros?.calories_max,
        custom_protein_min: clinicalMode.custom_macros?.protein_min,
        custom_protein_max: clinicalMode.custom_macros?.protein_max,
        medical_restrictions: clinicalMode.medical_restrictions || [],
        daily_sodium_limit: clinicalMode.daily_sodium_limit || null,
        daily_sugar_limit: clinicalMode.daily_sugar_limit || null,
        blood_pressure_tracking: clinicalMode.blood_pressure_tracking || false,
        locked_by_coach: clinicalMode.locked_by_coach || false
      });
      
      toast.success(language === 'fr' 
        ? 'Configuration sauvegardée'
        : 'Configuration saved');
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleRestriction = (restriction) => {
    setClinicalMode(prev => ({
      ...prev,
      custom_restrictions: prev.custom_restrictions.includes(restriction)
        ? prev.custom_restrictions.filter(r => r !== restriction)
        : [...prev.custom_restrictions, restriction]
    }));
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader>
          <button
            type="button"
            onClick={clinicalMode.enabled ? () => toggleClinicalMode(false) : handleEnableClick}
            disabled={saving}
            className="w-full flex items-center justify-between text-left"
            data-testid="clinical-mode-header-btn"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-500/10 rounded-lg">
                <Stethoscope className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading uppercase tracking-tight">
                  {language === 'fr' ? 'Mode Clinique' : 'Clinical Mode'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Pour les personnes sous protocole médical'
                    : 'For users under medical protocol'}
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={clinicalMode.enabled}
              onCheckedChange={clinicalMode.enabled ? () => toggleClinicalMode(false) : handleEnableClick}
              disabled={saving}
              data-testid="clinical-mode-toggle"
              onClick={(e) => e.stopPropagation()}
            />
          </button>
        </CardHeader>

        {clinicalMode.enabled && (
          <CardContent className="space-y-6 pt-0">
            {/* Warning Banner */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-500 mb-1">
                    {language === 'fr' ? 'Rappel Important' : 'Important Reminder'}
                  </p>
                  <p className="text-muted-foreground">
                    {language === 'fr'
                      ? 'Cette application ne remplace pas un avis médical. Consultez toujours votre professionnel de santé avant de modifier votre alimentation.'
                      : 'This app does not replace medical advice. Always consult your healthcare provider before changing your diet.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Clinical Profiles - Multiple Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium flex items-center gap-2">
                {language === 'fr' ? 'Profils Cliniques' : 'Clinical Profiles'}
                <span className="text-xs text-muted-foreground font-normal">
                  ({language === 'fr' ? 'Sélection multiple possible' : 'Multiple selection allowed'})
                </span>
              </label>
              <div className="grid gap-2">
                {CLINICAL_PROFILES.map(profile => {
                  const isSelected = clinicalMode.condition_types?.includes(profile.value);
                  return (
                    <button
                      key={profile.value}
                      onClick={() => toggleProfile(profile.value)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        isSelected 
                          ? `bg-${profile.color}-500/10 border-${profile.color}-500/50` 
                          : 'bg-secondary/50 border-transparent hover:border-muted-foreground/30'
                      }`}
                      data-testid={`profile-${profile.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isSelected ? `text-${profile.color}-400` : 'text-foreground'}`}>
                          {language === 'fr' ? profile.labelFr : profile.labelEn}
                        </span>
                        {isSelected && (
                          <Check className={`w-5 h-5 text-${profile.color}-500`} />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'fr' ? profile.descFr : profile.descEn}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Healthcare Provider */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {language === 'fr' ? 'Professionnel de santé (optionnel)' : 'Healthcare Provider (optional)'}
              </label>
              <Input
                value={clinicalMode.healthcare_provider || ''}
                onChange={(e) => setClinicalMode(prev => ({ ...prev, healthcare_provider: e.target.value }))}
                placeholder={language === 'fr' ? 'Ex: Dr. Martin - Oncologie' : 'E.g.: Dr. Smith - Oncology'}
                className="bg-secondary border-border"
                data-testid="healthcare-provider-input"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'fr'
                  ? 'YUMIX rappellera de valider les nouvelles recettes avec ce professionnel.'
                  : 'YUMIX will remind you to validate new recipes with this provider.'}
              </p>
            </div>

            {/* Dietary Restrictions by Category */}
            <div className="space-y-4">
              <label className="text-sm font-medium">
                {language === 'fr' ? 'Restrictions Alimentaires Détaillées' : 'Detailed Dietary Restrictions'}
              </label>
              {RESTRICTION_CATEGORIES.map(category => (
                <div key={category.category} className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    {language === 'fr' ? category.labelFr : category.labelEn}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {category.restrictions.map(restriction => {
                      const isActive = clinicalMode.custom_restrictions?.includes(restriction.value);
                      return (
                        <button
                          key={restriction.value}
                          onClick={() => toggleRestriction(restriction.value)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                            isActive
                              ? 'bg-pink-500/20 text-pink-400 border border-pink-500/50'
                              : 'bg-secondary text-muted-foreground border border-transparent hover:border-muted-foreground/30'
                          }`}
                        >
                          {isActive && '✓ '}
                          {language === 'fr' ? restriction.labelFr : restriction.labelEn}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Dietary Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {language === 'fr' ? 'Notes médicales / alimentaires' : 'Medical / Dietary Notes'}
              </label>
              <Textarea
                value={clinicalMode.dietary_notes || ''}
                onChange={(e) => setClinicalMode(prev => ({ ...prev, dietary_notes: e.target.value }))}
                placeholder={language === 'fr' 
                  ? 'Ex: Chimiothérapie en cours - nausées fréquentes, préférer les textures douces...'
                  : 'E.g.: Ongoing chemotherapy - frequent nausea, prefer soft textures...'}
                className="bg-secondary border-border min-h-[100px]"
                data-testid="dietary-notes-input"
              />
            </div>

            {/* Active Restrictions Summary */}
            {clinicalMode.custom_restrictions?.length > 0 && (
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
                <p className="text-sm font-medium text-pink-400 mb-2">
                  {language === 'fr' 
                    ? `${clinicalMode.custom_restrictions.length} restriction(s) active(s)`
                    : `${clinicalMode.custom_restrictions.length} active restriction(s)`}
                </p>
                <div className="flex flex-wrap gap-1">
                  {clinicalMode.custom_restrictions.map(r => (
                    <span key={r} className="text-xs px-2 py-0.5 bg-pink-500/20 rounded-full text-pink-300">
                      {r.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ==================== PROTOCOLE MÉDICAL ==================== */}
            <div className="border-t border-border pt-6 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 text-red-400" />
                <h4 className="text-base font-heading uppercase tracking-tight">
                  {language === 'fr' ? 'Protocole Médical' : 'Medical Protocol'}
                </h4>
                {clinicalMode.locked_by_coach && (
                  <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full font-bold uppercase">
                    {language === 'fr' ? 'Verrouillé par le Coach' : 'Locked by Coach'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Sodium Limit */}
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Limite sodium (mg/jour)' : 'Sodium limit (mg/day)'}
                  </label>
                  <input
                    type="number"
                    value={clinicalMode.daily_sodium_limit || ''}
                    onChange={(e) => setClinicalMode(prev => ({...prev, daily_sodium_limit: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="1500"
                    disabled={clinicalMode.locked_by_coach && user?.role !== 'coach' && user?.role !== 'admin'}
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm disabled:opacity-50"
                    data-testid="sodium-limit-input"
                  />
                </div>

                {/* Sugar Limit */}
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Limite sucre (g/jour)' : 'Sugar limit (g/day)'}
                  </label>
                  <input
                    type="number"
                    value={clinicalMode.daily_sugar_limit || ''}
                    onChange={(e) => setClinicalMode(prev => ({...prev, daily_sugar_limit: e.target.value ? parseInt(e.target.value) : null}))}
                    placeholder="25"
                    disabled={clinicalMode.locked_by_coach && user?.role !== 'coach' && user?.role !== 'admin'}
                    className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm disabled:opacity-50"
                    data-testid="sugar-limit-input"
                  />
                </div>
              </div>

              {/* Blood Pressure Tracking */}
              <div className="flex items-center justify-between mt-4 p-3 bg-secondary rounded-lg">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-red-400" />
                  <span className="text-sm">
                    {language === 'fr' ? 'Suivi de la tension artérielle' : 'Blood pressure tracking'}
                  </span>
                </div>
                <Switch
                  checked={clinicalMode.blood_pressure_tracking || false}
                  onCheckedChange={(v) => setClinicalMode(prev => ({...prev, blood_pressure_tracking: v}))}
                  disabled={clinicalMode.locked_by_coach && user?.role !== 'coach' && user?.role !== 'admin'}
                  data-testid="bp-tracking-switch"
                />
              </div>

              {/* Lock toggle — coach only */}
              {(user?.role === 'coach' || user?.role === 'admin') && (
                <div className="flex items-center justify-between mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-300">
                      {language === 'fr' ? 'Verrouiller (patient ne peut pas modifier)' : 'Lock (patient cannot modify)'}
                    </span>
                  </div>
                  <Switch
                    checked={clinicalMode.locked_by_coach || false}
                    onCheckedChange={(v) => setClinicalMode(prev => ({...prev, locked_by_coach: v}))}
                    data-testid="lock-by-coach-switch"
                  />
                </div>
              )}
            </div>

            {/* Info about Chef Mode */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ChefHat className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-primary mb-1">
                    {language === 'fr' ? 'Mode Chef Empathique Activé' : 'Empathetic Chef Mode Active'}
                  </p>
                  <p className="text-muted-foreground">
                    {language === 'fr'
                      ? 'YUMIX adoptera un ton doux et bienveillant, proposera des textures légères et des petits desserts santé pour apporter du plaisir à votre quotidien.'
                      : 'YUMIX will adopt a gentle, caring tone, suggest light textures and healthy desserts to bring joy to your daily routine.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={saveConfiguration}
              disabled={saving}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="save-clinical-config-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'Sauvegarder la configuration' : 'Save Configuration'}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              {language === 'fr' ? 'Décharge Légale' : 'Legal Disclaimer'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Veuillez lire et accepter les conditions suivantes pour activer le mode clinique.'
                : 'Please read and accept the following conditions to enable clinical mode.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Warning */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  {language === 'fr'
                    ? 'Cette application est un outil de logistique alimentaire. Elle ne fournit PAS de conseils médicaux et ne remplace PAS un professionnel de santé.'
                    : 'This application is a food logistics tool. It does NOT provide medical advice and does NOT replace a healthcare professional.'}
                </p>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="not-medical"
                  checked={acceptNotMedical}
                  onCheckedChange={setAcceptNotMedical}
                  className="mt-1"
                  data-testid="accept-not-medical-checkbox"
                />
                <label htmlFor="not-medical" className="text-sm cursor-pointer">
                  {language === 'fr'
                    ? 'Je comprends que cette application ne remplace pas un avis médical et que les suggestions ne sont pas des prescriptions médicales.'
                    : 'I understand that this app does not replace medical advice and that suggestions are not medical prescriptions.'}
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consult-healthcare"
                  checked={acceptConsultHealthcare}
                  onCheckedChange={setAcceptConsultHealthcare}
                  className="mt-1"
                  data-testid="accept-consult-healthcare-checkbox"
                />
                <label htmlFor="consult-healthcare" className="text-sm cursor-pointer">
                  {language === 'fr'
                    ? 'Je m\'engage à consulter mon professionnel de santé avant de modifier mon alimentation ou mes macros.'
                    : 'I commit to consulting my healthcare provider before changing my diet or macros.'}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDisclaimerModal(false)}
            >
              <X className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={acceptDisclaimer}
              disabled={saving || !acceptNotMedical || !acceptConsultHealthcare}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="accept-disclaimer-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'J\'accepte' : 'I Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClinicalModeSettings;
